import React from 'react';
import { Card, Divider, Typography, Tag } from 'antd';
import Html from 'Html';
import UploadFile, { UploadFileEntityTypes } from 'components/UploadFile/UploadFile';
import classNames from 'classnames';

const DocumentationRequirementCard = ({
  requirement,
  files,
  setFiles,
  application,
  readOnly = false,
  showRequirementStatus = false,
  showPersonName = false,
  title,
  className,
  descriptionsFooter,
  uploadFileProps,
}) => {
  const _hasFiles = files?.filter((file) => file.fileType === requirement.fileType) || [];

  const renderExtra = () => {
    if (_hasFiles.length !== 0) {
      return <UploadedTag count={_hasFiles.length} />;
    }

    if (requirement.optional) {
      return <OptionalTag />;
    }

    return <RequiredTag />;
  };

  const renderDescriptions = () => {
    if (!requirement?.descriptions?.length) {
      return descriptionsFooter;
    }

    if (requirement.descriptions.length === 1) {
      const description = requirement.descriptions[0];
      const content = typeof description === 'string' ? description : description.description;
      const personName = typeof description === 'object' ? description.personName : null;

      return (
        <>
          <Typography.Text type="secondary" className="block mb-5">
            {showPersonName && personName && <p className="block font-semibold text-black !mb-1">{personName}</p>}

            <Html html={content} />
          </Typography.Text>

          {descriptionsFooter}

          <Divider />
        </>
      );
    }

    const _shouldUseNumbering = requirement.descriptions.length > 1 && !showPersonName;
    return (
      <>
        <div className="mb-5">
          <ol className={classNames({ 'list-decimal pl-5': _shouldUseNumbering })}>
            {requirement.descriptions.map((description, descIndex) => {
              const content = typeof description === 'string' ? description : description.description;
              const personName = typeof description === 'object' ? description.personName : null;

              return (
                <li key={descIndex}>
                  <Typography.Text
                    type="secondary"
                    key={descIndex}
                    className={classNames('block', { 'mb-3': !_shouldUseNumbering })}
                  >
                    {showPersonName && personName && (
                      <p className="block font-semibold text-black !mb-1">{personName}</p>
                    )}

                    <Html html={content} />
                    {descIndex < requirement.descriptions.length - 1 && <br />}
                  </Typography.Text>
                </li>
              );
            })}
          </ol>
        </div>

        {descriptionsFooter}

        <Divider />
      </>
    );
  };

  return (
    <Card
      key={requirement.fileType}
      title={title}
      className={className}
      extra={showRequirementStatus ? renderExtra() : null}
    >
      {renderDescriptions()}

      <UploadFile
        entityType={UploadFileEntityTypes.FAMILY}
        fileCode={requirement.fileType}
        files={files}
        setFiles={setFiles}
        applicationId={application?.id}
        ariaLabel={`Upload ${requirement.title}`}
        buttonProps={{
          type: 'primary',
        }}
        readOnly={readOnly}
        {...uploadFileProps}
      />
    </Card>
  );
};

export default DocumentationRequirementCard;

export const OptionalTag = () => (
  <Tag bordered={false} className="text-md font-semibold !bg-[#efefef] !text-[#9a9a9a] py-1 px-2 rounded-2xl">
    If Applicable
  </Tag>
);

export const RequiredTag = ({ className }) => (
  <Tag
    bordered={false}
    className={classNames('text-md font-semibold !bg-[#ffeed7] !text-[#d55410] py-1 px-2 rounded-2xl', className)}
  >
    Required
  </Tag>
);

export const UploadedTag = ({ count }) => (
  <Typography.Text type="secondary" className="text-sm py-1 px-2">
    {count} file{count > 1 ? 's' : ''} uploaded
  </Typography.Text>
);
